/**
 * Oppgave-planlegger med drag-and-drop
 *
 * Visuell ukesplanlegging hvor oppgaver kan dras til spesifikke dager
 */

import React, { useState, useEffect } from 'react';
import './TaskPlanner.css';
import CalendarEventModal from './CalendarEventModal';
import AllokerOppgaveModal from './AllokerOppgaveModal';

const TaskPlanner = () => {
  const [tasks, setTasks] = useState([]);
  const [capacity, setCapacity] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedOppgave, setSelectedOppgave] = useState(null);
  const [showAllokerModal, setShowAllokerModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [draggedTask, setDraggedTask] = useState(null);
  const [autoAllocating, setAutoAllocating] = useState(false);
  const [editingAllocation, setEditingAllocation] = useState(null); // For redigeringsmodal

  const API_BASE = process.env.REACT_APP_API_URL || 'https://advokat-crm-api.swagner.workers.dev/api';

  useEffect(() => {
    console.log('🎯 TaskPlanner mounted!');
    loadData();
  }, []);

  // Funksjon for å slette gammel token og tvinge ny innlogging
  const refreshCalendarPermissions = () => {
    console.log('🔄 Sletter gammel access_token og tvinger ny innlogging...');
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_info');
    alert('Access token slettet! Du vil nå bli logget ut og må logge inn på nytt for å få kalendertilgang.');
    window.location.href = '/';
  };

  const loadData = async () => {
    setLoading(true);
    const token = localStorage.getItem('access_token');
    console.log('🔑 Token:', token ? 'Found' : 'Missing');

    // Cache-buster timestamp
    const timestamp = Date.now();

    // Hent oppgaver som trenger allokering (ikke crash hvis det feiler)
    try {
      const tasksRes = await fetch(`${API_BASE}/v1/capacity/tasks?_t=${timestamp}`, {
        method: 'GET',
        cache: 'no-store',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (tasksRes.ok) {
        const tasksData = await tasksRes.json();
        console.log('📋 Tasks response:', tasksData);
        setTasks(tasksData.oppgaver || []);
      } else {
        console.warn('⚠️ Kapasitetsoppgaver feilet:', tasksRes.status);
        setTasks([]);
      }
    } catch (error) {
      console.warn('⚠️ Kunne ikke hente kapasitetsoppgaver:', error);
      setTasks([]);
    }

    // Hent kapasitetsoversikt (ikke crash hvis det feiler)
    try {
      const capacityRes = await fetch(`${API_BASE}/v1/capacity/overview?days=14&_t=${timestamp}`, {
        method: 'GET',
        cache: 'no-store',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (capacityRes.ok) {
        const capacityData = await capacityRes.json();
        console.log('📅 Capacity overview response:', capacityData);
        console.log('📅 Number of days:', capacityData.dager?.length);

        // Log alle dager med oppgaver
        if (capacityData.dager) {
          capacityData.dager.forEach((day, index) => {
            if (day.oppgave_detaljer && day.oppgave_detaljer.length > 0) {
              console.log(`📅 Dag ${index + 1} (${day.dato_str}): ${day.oppgave_detaljer.length} oppgaver`, day.oppgave_detaljer);
            }
          });
        }

        setCapacity(capacityData.dager || []);
      } else {
        console.warn('⚠️ Kapasitetsoversikt feilet:', capacityRes.status);
        setCapacity([]);
      }
    } catch (error) {
      console.warn('⚠️ Kunne ikke hente kapasitetsoversikt:', error);
      setCapacity([]);
    }

    // Hent kalenderhendelser (7 dager) - ALLTID kjør dette selv om capacity feiler
    try {
      const calendarRes = await fetch(`${API_BASE}/calendar/events?days=7&_t=${timestamp}`, {
        method: 'GET',
        cache: 'no-store',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (calendarRes.ok) {
        const calendarData = await calendarRes.json();
        console.log('📅 Calendar events response:', calendarData);
        setCalendarEvents(calendarData.events || []);
      } else {
        console.warn('⚠️ Kalenderhendelser feilet:', calendarRes.status);
        setCalendarEvents([]);
      }
    } catch (calError) {
      console.warn('⚠️ Kunne ikke hente kalenderhendelser:', calError);
      setCalendarEvents([]);
    }

    setLoading(false);
  };

  // Automatisk allokering
  const handleAutoAllocate = async () => {
    setAutoAllocating(true);
    try {
      const token = localStorage.getItem('access_token');

      const res = await fetch(`${API_BASE}/capacity/auto-allocate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ days: 14, dry_run: false })
      });

      const result = await res.json();

      if (result.success) {
        alert(`✅ Allokerte ${result.allocated} av ${result.total} oppgaver automatisk!`);
        await loadData(); // Reload data
      } else {
        alert('Automatisk allokering feilet: ' + result.error);
      }

    } catch (error) {
      console.error('Auto-allokering feilet:', error);
      alert('Automatisk allokering feilet');
    } finally {
      setAutoAllocating(false);
    }
  };

  // Calendar event handlers
  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleNewEvent = () => {
    setSelectedEvent(null);
    setShowEventModal(true);
  };

  const handleEventSave = (savedEvent) => {
    // Oppdater listen med kalenderhendelser
    if (selectedEvent) {
      // Oppdatert eksisterende event
      setCalendarEvents(prev =>
        prev.map(e => e.id === savedEvent.id ? savedEvent : e)
      );
    } else {
      // Ny event lagt til
      setCalendarEvents(prev => [...prev, savedEvent]);
    }
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  const handleEventDelete = (deletedEventId) => {
    // Fjern event fra listen
    setCalendarEvents(prev =>
      prev.filter(e => e.id !== deletedEventId)
    );
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  // Oppgave allokering handlers
  const handleOppgaveClick = (oppgave) => {
    setSelectedOppgave(oppgave);
    setShowAllokerModal(true);
  };

  const handleOppgaveAlloker = (allokertOppgave) => {
    // Fjern fra uallokerte oppgaver
    setTasks(prev => prev.filter(t => t.id !== allokertOppgave.id));

    // Last inn kapasitetsdata på nytt for å oppdatere kalenderen
    loadData();

    setShowAllokerModal(false);
    setSelectedOppgave(null);
  };

  // Drag handlers
  const handleDragStart = (e, task) => {
    console.log('🎯 Drag started:', task);
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id || task.allokering_id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, day) => {
    e.preventDefault();
    console.log('🎯 Drop triggered on day:', day.dato_str, 'Dragged task:', draggedTask);

    if (!draggedTask) {
      console.log('⚠️ No dragged task!');
      return;
    }

    // Hvis det er en allokert oppgave som flyttes
    if (draggedTask.isAllocated) {
      // Sjekk om den droppes på samme dag
      if (draggedTask.dato_str === day.dato_str) {
        setDraggedTask(null);
        return; // Ingen endring
      }

      // Flytt til ny dato
      try {
        const token = localStorage.getItem('access_token');
        console.log('🚀 Sending update to backend:', {
          allokering_id: draggedTask.allokering_id,
          new_dato: day.dato_str,
          url: `${API_BASE}/capacity/allocation/${draggedTask.allokering_id}`
        });

        const res = await fetch(`${API_BASE}/capacity/allocation/${draggedTask.allokering_id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            dato: day.dato_str
          })
        });

        console.log('📡 Response status:', res.status, res.statusText);

        const result = await res.json();
        console.log('📥 Backend response:', result);

        if (result.success) {
          console.log('✅ Success! Reloading calendar...');
          await loadData();
          console.log('✅ Calendar reloaded!');
          // Valgfri: vis melding etter reload
          setTimeout(() => alert(`✅ Flyttet "${draggedTask.tittel}" til ${day.dato_str}`), 100);
        } else {
          console.error('❌ Backend error:', result);
          alert('Flytting feilet: ' + result.error);
        }

      } catch (error) {
        console.error('❌ Flytting feilet:', error);
        alert('Flytting feilet: ' + error.message);
      } finally {
        setDraggedTask(null);
      }
      return;
    }

    // Sjekk om det er nok kapasitet for ny allokering
    if (day.ledig_timer < draggedTask.estimert_timer) {
      alert(`⚠️ Ikke nok ledig tid denne dagen.\n\nLedig: ${day.ledig_timer}t\nTrenger: ${draggedTask.estimert_timer}t`);
      setDraggedTask(null);
      return;
    }

    // Allokér ny oppgave
    try {
      const token = localStorage.getItem('access_token');

      const res = await fetch(`${API_BASE}/capacity/manual-allocate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          oppgave_id: draggedTask.id,
          dato: day.dato_str
        })
      });

      const result = await res.json();

      if (result.success) {
        alert(`✅ Allokerte "${draggedTask.tittel}" til ${day.dato_str}`);
        await loadData(); // Reload
      } else {
        alert('Allokering feilet: ' + result.error);
      }

    } catch (error) {
      console.error('Allokering feilet:', error);
      alert('Allokering feilet');
    } finally {
      setDraggedTask(null);
    }
  };

  // Oppdater allokering
  const handleUpdateAllocation = async (allokering_id, updates) => {
    try {
      const token = localStorage.getItem('access_token');

      const res = await fetch(`${API_BASE}/capacity/allocation/${allokering_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      const result = await res.json();

      if (result.success) {
        alert('✅ Allokering oppdatert!');
        await loadData();
        setEditingAllocation(null);
      } else {
        alert('Oppdatering feilet: ' + result.error);
      }

    } catch (error) {
      console.error('Oppdatering feilet:', error);
      alert('Oppdatering feilet');
    }
  };

  // Slett allokering
  const handleDeleteAllocation = async (allokering_id) => {
    if (!window.confirm('Er du sikker på at du vil slette denne allokeringen?')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');

      const res = await fetch(`${API_BASE}/capacity/allocation/${allokering_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await res.json();

      if (result.success) {
        alert('✅ Allokering slettet!');
        await loadData();
        setEditingAllocation(null);
      } else {
        alert('Sletting feilet: ' + result.error);
      }

    } catch (error) {
      console.error('Sletting feilet:', error);
      alert('Sletting feilet');
    }
  };

  const getPrioritetBadge = (prioritet) => {
    const badges = {
      'kritisk': { text: '🔴 Kritisk', className: 'prioritet-kritisk' },
      'høy': { text: '🟠 Høy', className: 'prioritet-hoy' },
      'middels': { text: '🟡 Middels', className: 'prioritet-middels' },
      'lav': { text: '🟢 Lav', className: 'prioritet-lav' }
    };

    return badges[prioritet] || badges['middels'];
  };

  const getUtnyttelseFarge = (prosent) => {
    if (prosent >= 90) return 'utnyttelse-full';
    if (prosent >= 70) return 'utnyttelse-high';
    if (prosent >= 50) return 'utnyttelse-medium';
    return 'utnyttelse-low';
  };

  const formatDato = (datoStr) => {
    const date = new Date(datoStr);
    const dager = ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'];
    return {
      dag: dager[date.getDay()],
      dato: date.getDate(),
      maned: date.getMonth() + 1
    };
  };

  if (loading) {
    return (
      <div className="task-planner">
        <div className="loading">Laster oppgaveplanlegger...</div>
      </div>
    );
  }

  return (
    <div className="task-planner">
      <div className="planner-header">
        <h2>📅 Oppgaveplanlegger</h2>
        <div className="header-stats">
          <div className="stat">
            <strong>{tasks.length}</strong> uallokerte oppgaver
          </div>
          <div className="stat">
            <strong>{capacity.length}</strong> dager planlagt
          </div>
        </div>
        <button
          onClick={handleAutoAllocate}
          disabled={autoAllocating || tasks.length === 0}
          className="btn-auto-allocate"
        >
          {autoAllocating ? '⏳ Allokerer...' : '🤖 Auto-allokér'}
        </button>
      </div>

      {/* Kommende uke - Reell kalender */}
      <div className="calendar-week-view">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3>📅 Kommende uke - Kalender ({calendarEvents.length} hendelser)</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handleNewEvent}
              className="btn-new-event"
              style={{
                padding: '8px 16px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              ➕ Ny hendelse
            </button>
            {calendarEvents.length === 0 && (
              <button
                onClick={refreshCalendarPermissions}
                className="btn-refresh-permissions"
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                🔄 Forny kalendertilgang
              </button>
            )}
          </div>
        </div>
        {calendarEvents.length === 0 ? (
          <div className="empty-state">
            Ingen kalenderhendelser de neste 7 dagene
            <br />
            <small style={{ color: '#666', marginTop: '10px', display: 'block' }}>
              Hvis du har kalenderhendelser men de ikke vises, klikk "Forny kalendertilgang" ovenfor
            </small>
          </div>
        ) : (
          <div className="calendar-events-list">
            {calendarEvents.slice(0, 10).map(event => {
              const start = new Date(event.start);
              const end = new Date(event.end);
              const duration = (end - start) / (1000 * 60); // Minutter

              return (
                <div
                  key={event.id}
                  className="calendar-event-item clickable"
                  onClick={() => handleEventClick(event)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="event-time">
                    {start.toLocaleDateString('nb-NO', { weekday: 'short', day: 'numeric', month: 'short' })}
                    <br />
                    {start.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })} - {end.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div className="event-details">
                    <div className="event-subject">{event.subject}</div>
                    {event.location && <div className="event-location">📍 {event.location}</div>}
                    <div className="event-meta">{Math.round(duration)} min</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="planner-content">
        {/* Venstre panel: Uallokerte oppgaver */}
        <div className="task-pool">
          <h3>Uallokerte oppgaver ({tasks.length})</h3>
          <div className="task-list">
            {tasks.map(task => {
              const badge = getPrioritetBadge(task.prioritet);
              const forfall = task.forfall_dato ? new Date(task.forfall_dato).toLocaleDateString('nb-NO') : 'Ingen frist';

              return (
                <div
                  key={task.id}
                  className={`task-card ${selectedTask?.id === task.id ? 'selected' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, task)}
                  onClick={() => handleOppgaveClick(task)}
                  style={{ cursor: 'pointer' }}
                  title="Klikk for å allokere denne oppgaven"
                >
                  <div className={`task-prioritet ${badge.className}`}>
                    {badge.text}
                  </div>
                  <div className="task-tittel">{task.tittel}</div>
                  <div className="task-meta">
                    <span>⏱️ {task.estimert_timer}t</span>
                    <span>📅 {forfall}</span>
                  </div>
                  {task.kategori && (
                    <div className="task-kategori">{task.kategori}</div>
                  )}
                </div>
              );
            })}

            {tasks.length === 0 && (
              <div className="empty-state">
                ✅ Alle oppgaver er allokert!
              </div>
            )}
          </div>
        </div>

        {/* Høyre panel: Ukes-kalender */}
        <div className="week-calendar">
          <h3>Kapasitetskalender (14 dager)</h3>
          <div className="calendar-grid">
            {capacity.map(day => {
              const { dag, dato, maned } = formatDato(day.dato_str);
              const utnyttelseProsent = parseFloat(day.utnyttelsesgrad);
              const utnyttelseFarge = getUtnyttelseFarge(utnyttelseProsent);

              return (
                <div
                  key={day.dato_str}
                  className="day-slot"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, day)}
                >
                  <div className="day-header">
                    <div className="day-date">
                      <strong>{dag}</strong>
                      <span>{dato}/{maned}</span>
                    </div>
                    <div className={`day-utilization ${utnyttelseFarge}`}>
                      {utnyttelseProsent}%
                    </div>
                  </div>

                  <div className="day-stats">
                    <div className="stat-row">
                      <span>Total:</span>
                      <strong>{day.total_timer}t</strong>
                    </div>
                    <div className="stat-row">
                      <span>Møter:</span>
                      <span>{day.møter_timer}t</span>
                    </div>
                    <div className="stat-row">
                      <span>Allokert:</span>
                      <span>{day.allokert_timer}t</span>
                    </div>
                    <div className="stat-row ledig">
                      <span>Ledig:</span>
                      <strong>{day.ledig_timer}t</strong>
                    </div>
                  </div>

                  {day.oppgave_detaljer && day.oppgave_detaljer.length > 0 && (
                    <div className="day-oppgaver-list">
                      <div className="oppgaver-header">📋 Allokerte oppgaver:</div>
                      {day.oppgave_detaljer.map(oppgave => (
                        <div
                          key={oppgave.allokering_id}
                          className="allocated-task"
                          draggable
                          onDragStart={(e) => {
                            e.stopPropagation();
                            handleDragStart(e, {
                              ...oppgave,
                              dato_str: day.dato_str,
                              isAllocated: true
                            });
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingAllocation({
                              ...oppgave,
                              dato_str: day.dato_str
                            });
                          }}
                        >
                          <div className="allocated-task-title">{oppgave.tittel}</div>
                          <div className="allocated-task-meta">
                            <span>{oppgave.estimert_timer}t</span>
                            {oppgave.start_time && (
                              <span>{oppgave.start_time}-{oppgave.slutt_time}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Redigeringsmodal for allokert oppgave */}
      {editingAllocation && (
        <div className="task-detail-modal" onClick={() => setEditingAllocation(null)}>
          <div className="task-detail allocation-edit" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setEditingAllocation(null)}>×</button>
            <h3>Rediger allokering</h3>
            <p className="task-title-readonly">{editingAllocation.tittel}</p>
            <div className="form-group">
              <label>📅 Dato:</label>
              <input
                type="date"
                value={editingAllocation.dato_str || ''}
                onChange={(e) => setEditingAllocation({
                  ...editingAllocation,
                  dato_str: e.target.value
                })}
              />
            </div>
            <div className="form-group">
              <label>Antall timer:</label>
              <input
                type="number"
                step="0.5"
                min="0.5"
                max="8"
                value={editingAllocation.estimert_timer || 0}
                onChange={(e) => setEditingAllocation({
                  ...editingAllocation,
                  estimert_timer: parseFloat(e.target.value)
                })}
              />
            </div>
            <div className="form-group">
              <label>Starttidspunkt:</label>
              <input
                type="time"
                value={editingAllocation.start_time || '08:00'}
                onChange={(e) => setEditingAllocation({
                  ...editingAllocation,
                  start_time: e.target.value
                })}
              />
            </div>
            <div className="form-group">
              <label>Slutttidspunkt:</label>
              <input
                type="time"
                value={editingAllocation.slutt_time || '16:00'}
                onChange={(e) => setEditingAllocation({
                  ...editingAllocation,
                  slutt_time: e.target.value
                })}
              />
            </div>
            <div className="modal-actions">
              <button
                className="btn-save"
                onClick={() => handleUpdateAllocation(editingAllocation.allokering_id, {
                  allokerte_timer: editingAllocation.estimert_timer,
                  start_time: editingAllocation.start_time,
                  slutt_time: editingAllocation.slutt_time,
                  dato: editingAllocation.dato_str
                })}
              >
                💾 Lagre endringer
              </button>
              <button
                className="btn-unallocate"
                onClick={() => handleDeleteAllocation(editingAllocation.allokering_id)}
                title="Fjern allokering og send tilbake til uallokerte oppgaver"
              >
                ↩️ Tilbake til uallokert
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detalj-panel for valgt oppgave */}
      {selectedTask && (
        <div className="task-detail-modal" onClick={() => setSelectedTask(null)}>
          <div className="task-detail" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setSelectedTask(null)}>×</button>
            <h3>{selectedTask.tittel}</h3>
            <div className="detail-row">
              <strong>Prioritet:</strong>
              <span className={getPrioritetBadge(selectedTask.prioritet).className}>
                {getPrioritetBadge(selectedTask.prioritet).text}
              </span>
            </div>
            <div className="detail-row">
              <strong>Estimert tid:</strong>
              <span>{selectedTask.estimert_timer} timer</span>
            </div>
            <div className="detail-row">
              <strong>Kategori:</strong>
              <span>{selectedTask.kategori || 'Ingen'}</span>
            </div>
            {selectedTask.start_dato && (
              <div className="detail-row">
                <strong>Kan starte:</strong>
                <span>{new Date(selectedTask.start_dato).toLocaleDateString('nb-NO')}</span>
              </div>
            )}
            {selectedTask.forfall_dato && (
              <div className="detail-row">
                <strong>Frist:</strong>
                <span>{new Date(selectedTask.forfall_dato).toLocaleDateString('nb-NO')}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Calendar Event Modal */}
      {showEventModal && (
        <CalendarEventModal
          event={selectedEvent}
          onClose={() => setShowEventModal(false)}
          onSave={handleEventSave}
          onDelete={handleEventDelete}
        />
      )}

      {/* Alloker Oppgave Modal */}
      {showAllokerModal && (
        <AllokerOppgaveModal
          oppgave={selectedOppgave}
          onClose={() => setShowAllokerModal(false)}
          onAlloker={handleOppgaveAlloker}
        />
      )}
    </div>
  );
};

export default TaskPlanner;
